/**
 * CostAnalytics Component
 * Displays cost breakdown and analytics charts
 */

import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  ComposedChart
} from 'recharts';
import { format } from 'date-fns';
import { LLMStats } from '../../services/llm-monitoring';

interface CostAnalyticsProps {
  stats: LLMStats[];
  startDate: Date | null;
  endDate: Date | null;
}

const CostAnalytics: React.FC<CostAnalyticsProps> = ({ stats, startDate, endDate }) => {
  if (!stats || stats.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          No analytics data available for the selected period
        </Typography>
      </Box>
    );
  }

  // Prepare data for charts
  const chartData = stats.map(stat => ({
    date: format(new Date(stat.period), 'MMM dd'),
    cost: parseFloat(Number(stat.total_cost).toFixed(4)),
    requests: stat.total_requests,
    tokens: stat.total_tokens,
    avgLatency: Math.round(stat.avg_latency),
    successRate: (Number(stat.successful_requests) / Number(stat.total_requests) * 100).toFixed(1),
    cacheHitRate: stat.cache_hit_rate
  }));

  // Calculate summary metrics
  const summary = stats.reduce((acc, stat) => ({
    totalCost: acc.totalCost + stat.total_cost,
    totalRequests: acc.totalRequests + stat.total_requests,
    totalTokens: acc.totalTokens + stat.total_tokens,
    totalUsers: Math.max(acc.totalUsers, stat.unique_users || 0),
    avgCostPerRequest: 0,
    avgTokensPerRequest: 0
  }), {
    totalCost: 0,
    totalRequests: 0,
    totalTokens: 0,
    totalUsers: 0,
    avgCostPerRequest: 0,
    avgTokensPerRequest: 0
  });

  summary.avgCostPerRequest = summary.totalRequests > 0 
    ? summary.totalCost / summary.totalRequests 
    : 0;
  summary.avgTokensPerRequest = summary.totalRequests > 0 
    ? summary.totalTokens / summary.totalRequests 
    : 0;

  const formatCurrency = (value: number) => `$${value.toFixed(4)}`;
  const formatTokens = (value: number) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
    return value.toString();
  };

  return (
    <Box>
      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="caption">
                Total Cost
              </Typography>
              <Typography variant="h4">
                ${summary.totalCost.toFixed(2)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {startDate && endDate && 
                  `${format(startDate, 'MMM dd')} - ${format(endDate, 'MMM dd')}`
                }
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="caption">
                Total Requests
              </Typography>
              <Typography variant="h4">
                {summary.totalRequests.toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ${summary.avgCostPerRequest.toFixed(4)}/request
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="caption">
                Total Tokens
              </Typography>
              <Typography variant="h4">
                {formatTokens(summary.totalTokens)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {Math.round(summary.avgTokensPerRequest)} tokens/request
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="caption">
                Unique Users
              </Typography>
              <Typography variant="h4">
                {summary.totalUsers}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Active in period
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3}>
        {/* Cost Over Time */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Cost Over Time
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis tickFormatter={formatCurrency} />
                <Tooltip formatter={formatCurrency} />
                <Area 
                  type="monotone" 
                  dataKey="cost" 
                  stroke="#8884d8" 
                  fill="#8884d8" 
                  fillOpacity={0.6}
                />
              </AreaChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Requests Over Time */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Requests Over Time
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Line 
                  type="monotone" 
                  dataKey="requests" 
                  stroke="#82ca9d" 
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Token Usage */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Token Usage
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis tickFormatter={(value) => formatTokens(value)} />
                <Tooltip formatter={(value: any) => formatTokens(value)} />
                <Bar dataKey="tokens" fill="#ffc658" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Performance Metrics */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Performance Metrics
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip />
                <Legend />
                <Bar 
                  yAxisId="left" 
                  dataKey="avgLatency" 
                  fill="#8884d8" 
                  name="Avg Latency (ms)"
                />
                <Line 
                  yAxisId="right" 
                  type="monotone" 
                  dataKey="successRate" 
                  stroke="#82ca9d" 
                  name="Success Rate (%)"
                />
                <Line 
                  yAxisId="right" 
                  type="monotone" 
                  dataKey="cacheHitRate" 
                  stroke="#ff7300" 
                  name="Cache Hit Rate (%)"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Daily Cost Breakdown */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Daily Breakdown
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <Grid container spacing={1} sx={{ minWidth: 800, p: 1 }}>
                {chartData.map((day, index) => (
                  <Grid item key={index}>
                    <Card variant="outlined" sx={{ p: 1, minWidth: 120 }}>
                      <Typography variant="caption" color="text.secondary">
                        {day.date}
                      </Typography>
                      <Typography variant="h6">${day.cost}</Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                        <Chip size="small" label={`${day.requests} req`} />
                        <Chip size="small" label={`${formatTokens(day.tokens)}`} />
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CostAnalytics;